import { ContractStatusEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useAccess } from 'hooks/useAccess';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import type { Position } from 'uikit';
import { Button, cnMixSpace, Tooltip } from 'uikit';

import type { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { WorkOrderChapters } from '../workorderTypes';

import type { IAddActKC2ModalProps } from './AddActKC2Modal';
import { AddActKC2Modal } from './AddActKC2Modal';

import './AddActKC2.css';

interface IAddActKC2Props extends Omit<IAddActKC2ModalProps, 'onClose'> {
    gridTableRef: AgGridReact<any> | null;
    onAdd: (actId?: number) => void;
    statusContract?: ContractStatusEnum;
    statusWorkOrder?: WorkOrderStatusEnum;
    workOrderObjectId?: number;
    isDisabled?: boolean;
    isOpenedOuter?: boolean;
    closeOuterHandler?: () => void;
    withBtn?: boolean;
    tooltipPosition?: Position;
    tooltipOuterOpened?: boolean;
}

export const AddActKC2 = (props: IAddActKC2Props) => {
    const { id: projectId, orderId } = useParams();
    const {
        objectId,
        chapter,
        statusWorkOrder,
        onAdd,
        statusContract,
        workOrderObjectId,
        isDisabled,
        isOpenedOuter,
        closeOuterHandler,
        withBtn = true,
        tooltipPosition,
        tooltipOuterOpened,
    } = props;

    const { projectData } = useProjectData(projectId, false);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const buttonRef = useRef(null);
    const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
    const [isOpenTooltip, setIsOpenTooltip] = useState<boolean>(false);

    const { aktKC2: aktKC2Rules } = useAccess();

    const isCanCreateAktKC2 = useMemo(() => aktKC2Rules.canCreate, [aktKC2Rules.canCreate]);

    const handleMouseMoveButton = useCallback(() => {
        if (!isCanCreateAktKC2) {
            setIsOpenTooltip(true);
        }
    }, [isCanCreateAktKC2]);

    const handleMouseLeaveButton = useCallback(() => {
        setIsOpenTooltip(false);
    }, []);

    const handleOnButtonClick = useCallback(() => {
        setIsOpenModal(true);

        sendGpnAnalyticsEvent({
            componentId: 'rim_add act KC button_pressed',
            projectId,
            orderId: Number(orderId),
            methodology: projectData?.methodology,
            projectName: projectData?.name,
            projectType: projectData?.contractType,
            projectDO: projectData?.subsidiary?.name,
        });
    }, [
        orderId,
        projectData?.contractType,
        projectData?.methodology,
        projectData?.name,
        projectData?.subsidiary?.name,
        projectId,
        sendGpnAnalyticsEvent,
    ]);

    const handleOnCloseModal = useCallback(
        (hasSaved, actId) => {
            if (closeOuterHandler) {
                closeOuterHandler();
            } else {
                setIsOpenModal(false);
            }

            if (hasSaved) {
                onAdd(actId);
            }
        },
        [closeOuterHandler, onAdd],
    );

    const isContractStatusWork = statusContract === ContractStatusEnum.InImplementation;

    const isStatusWorkOrderOnFormation = statusWorkOrder === WorkOrderStatusEnum.OnFormation;
    const isStatusWorkOrderInProgress = statusWorkOrder === WorkOrderStatusEnum.InProgress;
    const isStatusWorkOrderDelayed = statusWorkOrder === WorkOrderStatusEnum.Delayed;

    const isDisabledBtn =
        !objectId ||
        isStatusWorkOrderOnFormation ||
        isStatusWorkOrderDelayed ||
        chapter === WorkOrderChapters.SUMMARY ||
        (!chapter && !workOrderObjectId) ||
        !isContractStatusWork ||
        !isCanCreateAktKC2 ||
        isDisabled;

    const textTooltip = useMemo(() => {
        if (isStatusWorkOrderInProgress && !(objectId && chapter)) {
            return 'Для создания Акта КС-2 выберите раздел и объект';
        }

        if (!isStatusWorkOrderInProgress) {
            return 'Добавление Акта КС-2 доступно только в статусе НЗ "В работе"';
        }

        return 'У вас недостаточно прав для создания Акта КС-2';
    }, [chapter, isStatusWorkOrderInProgress, objectId]);

    return (
        <>
            {withBtn && (
                <div onMouseMove={handleMouseMoveButton} onMouseLeave={handleMouseLeaveButton}>
                    <Button
                        className={cnMixSpace({ mR: 'xs' })}
                        label="Добавить Акт КС-2"
                        view="secondary"
                        size="s"
                        onClick={handleOnButtonClick}
                        disabled={isDisabledBtn}
                        ref={buttonRef}
                    />
                </div>
            )}
            {(isOpenModal || isOpenedOuter) && <AddActKC2Modal {...props} onClose={handleOnCloseModal} />}

            {(isOpenTooltip || tooltipOuterOpened) && (
                <Tooltip
                    size="s"
                    direction="rightCenter"
                    anchorRef={buttonRef}
                    position={tooltipPosition}
                    isInteractive={false}
                    style={{ zIndex: 2 }}
                >
                    {textTooltip}
                </Tooltip>
            )}
        </>
    );
};

AddActKC2.displayName = 'AddActKC2';
